import axios, { AxiosError } from 'axios';
import { ApiErrorDataDtoSchema } from './api.contracts';
import { normalizeValidationErrors } from './api.lib';
import { env } from '~shared/config/env';
import { oneIdentifier, isStaging, isLocal } from '~shared/config/hostname';

const LOCAL_API_URL = 'http://localhost:18080/rest/v1/samera-one';

export const api = axios.create({
  baseURL: `${
    isLocal
      ? LOCAL_API_URL
      : isStaging
      ? env.STAGING_API_URL ?? env.API_URL
      : env.API_URL
  }/${oneIdentifier}`,
});

export const attachAuthInterceptor = (
  getSessionId: () => string | undefined,
) => {
  api.interceptors.request.use(
    config => {
      const sessionId = getSessionId();
      if (sessionId) {
        config.headers['x-samera-session-id'] = sessionId;
      }
      return config;
    },
    error => Promise.reject(error),
  );
};

api.interceptors.response.use(
  response => response,
  error => {
    if (!axios.isAxiosError(error)) {
      // Ensure we always reject with an Error object
      if (error instanceof Error) {
        return Promise.reject(error);
      }
      // Convert non-Error rejections to Error objects
      const errorMessage =
        typeof error === 'string'
          ? error
          : typeof error === 'object' && error !== null
          ? JSON.stringify(error)
          : 'Unknown error';
      return Promise.reject(new Error(errorMessage));
    }

    const validation = ApiErrorDataDtoSchema.safeParse(error.response?.data);

    if (!validation.success) {
      // If the error response doesn't match our schema, ensure we still return a proper Error
      // Extract error message from response data if it's a string or object
      let errorMessage = error.message;
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (
          typeof error.response.data === 'object' &&
          error.response.data !== null
        ) {
          // Try to extract a meaningful message
          const data = error.response.data as Record<string, unknown>;
          errorMessage =
            (data.message as string) ||
            (data.error as string) ||
            JSON.stringify(data) ||
            errorMessage;
        }
      }

      const enhancedError = new AxiosError(
        errorMessage,
        error.code,
        error.config,
        error.request,
        error.response,
      );
      return Promise.reject(enhancedError);
    }

    const normalizedErrorResponse = {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...error.response!,
      data: normalizeValidationErrors(validation.data),
    };

    const normalizedError = new AxiosError(
      error.message,
      error.code,
      error.config,
      error.request,
      normalizedErrorResponse,
    );

    return Promise.reject(normalizedError);
  },
);
